import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table2 } from 'lucide-react';
import styles from './ErpDataGrid.module.css';
import SearchFilterBar from './SearchFilterBar';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

const BLANK_BUFFER = 8;
const ORG_PAGE_SIZE = 1000;
const MAX_COLUMN_PX = 192;
const CELL_CHROME_PX = 20;
const HEADER_FONT = "500 11.5px 'JetBrains Mono', ui-monospace, monospace";
const CELL_FONT = "400 13.5px Inter, sans-serif";

const measureText = (text, font) => {
  if (typeof document === 'undefined') return String(text || '').length * 8;
  if (!measureText.canvas) measureText.canvas = document.createElement('canvas');
  const context = measureText.canvas.getContext('2d');
  if (!context) return String(text || '').length * 8;
  context.font = font;
  return context.measureText(String(text || '')).width;
};

async function parseJsonResponse(response, fallbackMessage) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new Error(
        response.status === 404
          ? `${fallbackMessage}: API route not found. Restart the server.`
          : `${fallbackMessage} (HTTP ${response.status})`
      );
    }
    throw new Error(text || `${fallbackMessage} (HTTP ${response.status})`);
  }
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `${fallbackMessage} (HTTP ${response.status})`);
  }
  return data;
}

const isBlankRow = (row, columns) =>
  columns.every((column) => {
    const value = row.values?.[column.name];
    if (value === null || value === undefined || value === '') return true;
    if (typeof value === 'object') return !value.ref_record_id && !value.label;
    return String(value).trim() === '';
  });

/** HTML date inputs require YYYY-MM-DD. Server stores MM-DD-YYYY (and some rows may be DD-MM-YYYY). */
const toDateInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const parts = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (!parts) return '';

  const first = parseInt(parts[1], 10);
  const second = parseInt(parts[2], 10);
  const year = parts[3];
  if (!Number.isFinite(first) || !Number.isFinite(second)) return '';

  // Stored format is MM-DD-YYYY; if the first number cannot be a month, treat as DD-MM-YYYY.
  if (first > 12 && second <= 12) {
    return `${year}-${String(second).padStart(2, '0')}-${String(first).padStart(2, '0')}`;
  }
  return `${year}-${String(first).padStart(2, '0')}-${String(second).padStart(2, '0')}`;
};

const normalizeDateValues = (values, columns) => {
  if (!values || !columns?.length) return values || {};
  const next = { ...values };
  columns.forEach((column) => {
    if (column.type === 'date') {
      next[column.name] = toDateInputValue(next[column.name]);
    }
  });
  return next;
};

const mergePersistedValues = (currentValues, serverValues, columns) => {
  const server = normalizeDateValues(serverValues || {}, columns);
  const merged = { ...server, ...(currentValues || {}) };
  (columns || []).forEach((column) => {
    if (column.seq !== true) return;
    const clientVal = currentValues?.[column.name];
    if (clientVal == null || String(clientVal).trim() === '') {
      merged[column.name] = server[column.name] ?? '';
    }
  });
  return normalizeDateValues(merged, columns);
};

const displayCellValue = (value, column) => {
  if (value === null || value === undefined) return '';
  if (column.type === 'reference') {
    if (typeof value === 'object') return value.label || '';
    return String(value);
  }
  if (column.type === 'date') {
    return toDateInputValue(value);
  }
  if (column.type === 'boolean') {
    if (value === true || value === 'true' || value === 'Yes') return 'Yes';
    if (value === false || value === 'false' || value === 'No') return 'No';
  }
  return String(value);
};

const cellFilterKey = (value, column) => {
  if (value === null || value === undefined || value === '') return '';
  if (column.type === 'reference') {
    if (typeof value === 'object') {
      return value.ref_record_id ? String(value.ref_record_id) : '';
    }
    return String(value);
  }
  return String(displayCellValue(value, column)).trim();
};

const isTextInput = (element) =>
  element.tagName === 'INPUT' && (element.type === 'text' || element.type === '');

/** Leave a text cell only at the caret edges, or when the whole value is selected. */
const shouldLeaveTextCell = (element, direction) => {
  if (!isTextInput(element) || element.readOnly) return true;
  const start = element.selectionStart;
  const end = element.selectionEnd;
  if (start == null || end == null) return true;
  const length = element.value.length;
  const allSelected = length > 0 && start === 0 && end === length;
  if (allSelected) return true;
  if (direction === 'left') return start === 0 && end === 0;
  return start === length && end === length;
};

const selectCellText = (control, isEditing) => {
  if (!isTextInput(control)) return;
  const apply = () => {
    if (document.activeElement !== control || isEditing()) return;
    control.select();
  };
  apply();
  requestAnimationFrame(apply);
};

const scrollGridCellIntoView = (cell, container) => {
  if (!cell || !container) return;
  const containerRect = container.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();
  const header = container.querySelector('thead');
  const headerHeight = header ? header.getBoundingClientRect().height : 0;

  let stickyRight = containerRect.left;
  const row = cell.parentElement;
  if (row) {
    Array.from(row.children).forEach((sibling) => {
      if (sibling === cell) return;
      if (window.getComputedStyle(sibling).position !== 'sticky') return;
      stickyRight = Math.max(stickyRight, sibling.getBoundingClientRect().right);
    });
  }

  const topLimit = containerRect.top + headerHeight;
  const leftLimit = stickyRight;
  let deltaY = 0;
  let deltaX = 0;

  if (cellRect.top < topLimit) deltaY = cellRect.top - topLimit;
  else if (cellRect.bottom > containerRect.bottom) deltaY = cellRect.bottom - containerRect.bottom;

  if (cellRect.left < leftLimit) deltaX = cellRect.left - leftLimit;
  else if (cellRect.right > containerRect.right - 1) deltaX = cellRect.right - (containerRect.right - 1);

  if (deltaX !== 0 || deltaY !== 0) {
    container.scrollBy({ top: deltaY, left: deltaX, behavior: 'auto' });
  }
};

const ErpDataGrid = ({
  schemaId,
  analyticsTable = null,
  readOnly = false,
  isOrgTemplate = false,
  selectMode = false,
  selectedIds = [],
  onSelectedIdsChange,
  onOpenEditFields,
  onSelectMaster,
}) => {
  const [schema, setSchema] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [referenceOptions, setReferenceOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [saveFlash, setSaveFlash] = useState(false);
  const saveTimers = useRef({});
  const rowsRef = useRef([]);
  const saveFlashTimer = useRef(null);
  const persistLocks = useRef({});
  const rowEpoch = useRef({});
  const gridScrollRef = useRef(null);
  const editingCellRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const usesOrgPaging = Boolean(analyticsTable) || isOrgTemplate;

  const columns = useMemo(
    () => schema?.schema_definition?.columns || [],
    [schema]
  );

  const searchColumns = useMemo(
    () => columns.filter((column) => column.search === true),
    [columns]
  );

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const ensureBlankBuffer = useCallback((existingRows, cols) => {
    const next = [...existingRows];
    let trailing = 0;
    for (let i = next.length - 1; i >= 0; i -= 1) {
      if (isBlankRow(next[i], cols)) trailing += 1;
      else break;
    }
    while (trailing < BLANK_BUFFER) {
      next.push({
        localId: `blank-${Date.now()}-${trailing}-${Math.random()}`,
        recordId: null,
        values: Object.fromEntries(cols.map((col) => [col.name, ''])),
        dirty: false,
      });
      trailing += 1;
    }
    return next;
  }, []);

  const fetchReferenceOptions = useCallback(
    async (cols) => {
        const referenceColumns = cols.filter(
        (column) => column.type === 'reference' && column.ref_schema_id
      );
      if (referenceColumns.length === 0) {
        setReferenceOptions({});
        return;
      }

      const entries = await Promise.all(
        referenceColumns.map(async (column) => {
          try {
            const response = await fetch(
              `${API_URL}/schemas/${column.ref_schema_id}/reference-options`,
              { headers: authHeaders() }
            );
            const data = await parseJsonResponse(response, 'Failed to load reference options');
            const options = (data.data?.options || [])
              .map((option) => ({
                record_id: option.record_id,
                label: String(option.label || '').trim(),
              }))
              .filter((option) => option.record_id && option.label);
            return [column.name, options];
          } catch (err) {
            return [column.name, []];
          }
        })
      );
      setReferenceOptions(Object.fromEntries(entries));
    },
    [authHeaders]
  );

  const loadGrid = useCallback(async () => {
    if (!schemaId && !analyticsTable) return;
    const quiet = hasLoadedRef.current;
    if (quiet) setPageLoading(true);
    else setLoading(true);
    setError('');
    try {
      if (analyticsTable) {
        const response = await fetch(
          `${API_URL}/visuals/analytics-tables/${encodeURIComponent(analyticsTable)}?limit=${ORG_PAGE_SIZE}&page=${page}`,
          { headers: authHeaders() }
        );
        const data = await parseJsonResponse(response, 'Failed to load analytics table');
        const cols = data.data?.columns || [];
        const records = data.data?.rows || [];
        const mappedRows = records.map((record, index) => ({
          localId: String(record.id ?? `${page}-${index}`),
          recordId: record.id ?? (page - 1) * ORG_PAGE_SIZE + index + 1,
          values: normalizeDateValues(record.values || {}, cols),
          dirty: false,
        }));
        setSchema({
          table_name: data.data?.table_name || analyticsTable,
          schema_definition: { columns: cols },
        });
        setTotalRecords(Number(data.data?.totalRecords ?? mappedRows.length));
        rowsRef.current = mappedRows;
        setRows(mappedRows);
        setReferenceOptions({});
        hasLoadedRef.current = true;
        return;
      }

      const requestLimit = isOrgTemplate ? ORG_PAGE_SIZE : 1000;
      const requestPage = isOrgTemplate ? page : 1;
      const [schemaRes, dataRes] = await Promise.all([
        fetch(`${API_URL}/schemas/${schemaId}`, { headers: authHeaders() }),
        fetch(
          `${API_URL}/visuals/data?schema_id=${schemaId}&limit=${requestLimit}&page=${requestPage}`,
          { headers: authHeaders() }
        ),
      ]);

      const schemaJson = await parseJsonResponse(schemaRes, 'Failed to load schema');
      const dataJson = await parseJsonResponse(dataRes, 'Failed to load data');
      const schemaData = schemaJson.data;
      const cols = schemaData?.schema_definition?.columns || [];
      const records = dataJson.data?.records || [];

      const mappedRows = records.map((record) => ({
        localId: String(record.record_id),
        recordId: record.record_id,
        values: normalizeDateValues(record.record_data || {}, cols),
        dirty: false,
      }));

      setSchema(schemaData);
      setTotalRecords(
        isOrgTemplate ? Number(dataJson.data?.totalRecords ?? mappedRows.length) : 0
      );
      const buffered = readOnly ? mappedRows : ensureBlankBuffer(mappedRows, cols);
      rowsRef.current = buffered;
      setRows(buffered);
      await fetchReferenceOptions(cols);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err.message || 'Failed to load table');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [
    schemaId,
    analyticsTable,
    isOrgTemplate,
    page,
    readOnly,
    authHeaders,
    ensureBlankBuffer,
    fetchReferenceOptions,
  ]);

  useEffect(() => {
    loadGrid();
    return () => {
      Object.values(saveTimers.current).forEach((timer) => clearTimeout(timer));
      saveTimers.current = {};
      if (saveFlashTimer.current) clearTimeout(saveFlashTimer.current);
    };
  }, [loadGrid]);

  useEffect(() => {
    setFilters({});
  }, [schemaId, analyticsTable]);

  const persistRow = useCallback(
    async (row) => {
      const key = row?.localId;
      if (!schemaId || !columns.length || !key) return;

      const existingLock = persistLocks.current[key];
      if (existingLock) {
        existingLock.queued = true;
        return existingLock.promise;
      }

      const lock = { queued: false, promise: null };
      lock.promise = (async () => {
        try {
          do {
            lock.queued = false;
            const latest = rowsRef.current.find((item) => item.localId === key);
            if (!latest) return;
            if (isBlankRow(latest, columns) && !latest.recordId) return;

            const epoch = rowEpoch.current[key] || 0;
            const payloadData = {};
            columns.forEach((column) => {
              const value = latest.values?.[column.name];
              if (column.type === 'reference') {
                if (typeof value === 'object' && value?.ref_record_id) {
                  payloadData[column.name] = { ref_record_id: value.ref_record_id };
                } else if (value) {
                  payloadData[column.name] = { ref_record_id: value };
                } else {
                  payloadData[column.name] = null;
                }
              } else if (column.type === 'boolean') {
                if (value === 'Yes' || value === true || value === 'true') {
                  payloadData[column.name] = true;
                } else if (value === 'No' || value === false || value === 'false') {
                  payloadData[column.name] = false;
                } else {
                  payloadData[column.name] = value || '';
                }
              } else if (column.type === 'date') {
                payloadData[column.name] = toDateInputValue(value) || '';
              } else {
                payloadData[column.name] = value ?? '';
              }
            });

            setSavingKey(key);
            const applyPatch = (patchFn, withBuffer = false) => {
              const immediate = rowsRef.current.map((item) =>
                item.localId === key ? patchFn(item) : item
              );
              rowsRef.current = withBuffer
                ? ensureBlankBuffer(immediate, columns)
                : immediate;
              setRows((prev) => {
                const next = prev.map((item) =>
                  item.localId === key ? patchFn(item) : item
                );
                const buffered = withBuffer ? ensureBlankBuffer(next, columns) : next;
                rowsRef.current = buffered;
                return buffered;
              });
            };

            if (latest.recordId) {
              const response = await fetch(`${API_URL}/data/${latest.recordId}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                  schema_id: schemaId,
                  data: payloadData,
                }),
              });
              const data = await parseJsonResponse(response, 'Failed to update row');
              applyPatch((item) => ({
                ...item,
                values: mergePersistedValues(item.values, data.data?.record_data, columns),
                dirty: (rowEpoch.current[key] || 0) !== epoch,
              }));
            } else {
              const response = await fetch(`${API_URL}/data`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                  schema_id: schemaId,
                  data: payloadData,
                }),
              });
              const data = await parseJsonResponse(response, 'Failed to create row');
              applyPatch(
                (item) => ({
                  ...item,
                  recordId: data.data?.record_id || item.recordId,
                  values: mergePersistedValues(item.values, data.data?.record_data, columns),
                  dirty: (rowEpoch.current[key] || 0) !== epoch,
                }),
                true
              );
            }
            setError('');
            setSaveFlash(true);
            if (saveFlashTimer.current) clearTimeout(saveFlashTimer.current);
            saveFlashTimer.current = setTimeout(() => setSaveFlash(false), 900);
          } while (lock.queued);
        } catch (err) {
          setError(err.message || 'Failed to save row');
        } finally {
          if (persistLocks.current[key] === lock) {
            delete persistLocks.current[key];
          }
          setSavingKey(null);
        }
      })();

      persistLocks.current[key] = lock;
      return lock.promise;
    },
    [schemaId, columns, authHeaders, ensureBlankBuffer]
  );

  const scheduleSave = useCallback(
    (row) => {
      const key = row.localId;
      if (saveTimers.current[key]) {
        clearTimeout(saveTimers.current[key]);
      }
      saveTimers.current[key] = setTimeout(() => {
        const latest = rowsRef.current.find((item) => item.localId === key);
        if (latest) persistRow(latest);
      }, 450);
    },
    [persistRow]
  );

  const handleCellChange = (localId, column, rawValue) => {
    let nextValue = rawValue;
    if (column.type === 'reference') {
      const option = (referenceOptions[column.name] || []).find(
        (item) => String(item.record_id) === String(rawValue)
      );
      nextValue = rawValue
        ? { ref_record_id: rawValue, label: option?.label || '' }
        : null;
    }

    const current = rowsRef.current.find((item) => item.localId === localId);
    if (!current) return;
    if (column.seq === true && current.values?.[column.name]) return;

    const key = current.localId;
    rowEpoch.current[key] = (rowEpoch.current[key] || 0) + 1;

    setRows((prev) => {
      const index = prev.findIndex((row) => row.localId === key);
      const source = index >= 0 ? prev : rowsRef.current;
      const sourceIndex = index >= 0 ? index : source.findIndex((row) => row.localId === key);
      if (sourceIndex < 0) return prev;
      const row = source[sourceIndex];
      const updatedRow = {
        ...row,
        dirty: true,
        values: {
          ...row.values,
          [column.name]: nextValue,
        },
      };
      const nextRows = source.map((item, i) => (i === sourceIndex ? updatedRow : item));
      const buffered = ensureBlankBuffer(nextRows, columns);
      rowsRef.current = buffered;
      return buffered;
    });
    scheduleSave({ localId: key });
  };

  const handleCellBlur = (localId) => {
    const row = rowsRef.current.find((item) => item.localId === localId);
    if (!row || !row.dirty) return;
    scheduleSave(row);
  };

  const hasActiveFilters = searchColumns.some((column) => Boolean(filters[column.name]));

  const filterOptions = useMemo(() => {
    const map = {};
    searchColumns.forEach((column) => {
      const seen = new Map();
      rows.forEach((row) => {
        if (!row.recordId) return;
        const raw = row.values?.[column.name];
        const key = cellFilterKey(raw, column);
        if (!key) return;
        let label = String(displayCellValue(raw, column) || '').trim();
        if (!label && column.type === 'reference') {
          const option = (referenceOptions[column.name] || []).find(
            (item) => String(item.record_id) === String(key)
          );
          label = option?.label || '';
        }
        if (!label) return;
        if (!seen.has(String(key))) {
          seen.set(String(key), { id: String(key), label });
        }
      });
      map[column.name] = Array.from(seen.values());
    });
    return map;
  }, [searchColumns, rows, referenceOptions]);

  const visibleRows = useMemo(() => {
    if (!hasActiveFilters) return rows;
    return rows.filter((row) => {
      if (!row.recordId) return false;
      return searchColumns.every((column) => {
        const selected = filters[column.name];
        if (!selected) return true;
        return String(cellFilterKey(row.values?.[column.name], column)) === String(selected);
      });
    });
  }, [rows, filters, searchColumns, hasActiveFilters]);

  const columnWidths = useMemo(() => {
    const map = {};
    columns.forEach((column) => {
      const headerExtra = column.type === 'reference' && column.ref_schema_id ? 18 : 0;
      const headerPx =
        measureText(String(column.name || '').toUpperCase(), HEADER_FONT) +
        CELL_CHROME_PX +
        headerExtra;
      let contentPx = 0;
      visibleRows.forEach((row) => {
        const label = String(displayCellValue(row.values?.[column.name], column) || '').trim();
        if (!label) return;
        contentPx = Math.max(contentPx, measureText(label, CELL_FONT) + CELL_CHROME_PX);
      });
      const fitted = Math.max(headerPx, contentPx);
      const cap = Math.max(headerPx, MAX_COLUMN_PX);
      map[column.name] = Math.ceil(Math.min(fitted, cap));
    });
    return map;
  }, [columns, visibleRows]);

  const persistedRows = visibleRows.filter((row) => row.recordId);
  const selectedIdSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );
  const allPersistedSelected =
    persistedRows.length > 0 &&
    persistedRows.every((row) => selectedIdSet.has(String(row.recordId)));

  const toggleRowSelected = (recordId) => {
    if (!onSelectedIdsChange || !recordId) return;
    const id = String(recordId);
    if (selectedIdSet.has(id)) {
      onSelectedIdsChange(selectedIds.filter((item) => String(item) !== id));
    } else {
      onSelectedIdsChange([...selectedIds, id]);
    }
  };

  const toggleAllSelected = () => {
    if (!onSelectedIdsChange) return;
    if (allPersistedSelected) {
      onSelectedIdsChange([]);
      return;
    }
    onSelectedIdsChange(persistedRows.map((row) => String(row.recordId)));
  };

  const inputTypeFor = (type) => {
    if (type === 'integer') return 'number';
    if (type === 'date') return 'date';
    return 'text';
  };

  const focusCell = (rowIndex, colIndex) => {
    const root = gridScrollRef.current;
    if (!root || rowIndex < 0 || colIndex < 0) return;
    if (rowIndex >= visibleRows.length || colIndex >= columns.length) return;
    const cell = root.querySelector(`td[data-row="${rowIndex}"][data-col="${colIndex}"]`);
    const control = cell?.querySelector('input, select');
    if (!control) return;
    editingCellRef.current = false;
    control.focus({ preventScroll: true });
    selectCellText(control, () => editingCellRef.current);
    scrollGridCellIntoView(cell, root);
  };

  const handleGridPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const control = target.closest('input, select');
    if (!control || !isTextInput(control)) return;

    const alreadyFocused = document.activeElement === control;
    if (!alreadyFocused) {
      event.preventDefault();
      editingCellRef.current = false;
      control.focus({ preventScroll: true });
      selectCellText(control, () => editingCellRef.current);
      return;
    }

    if (!editingCellRef.current) {
      editingCellRef.current = true;
    }
  };

  const handleGridPointerUp = (event) => {
    if (editingCellRef.current) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const control = target.closest('input, select');
    if (!control || !isTextInput(control) || document.activeElement !== control) return;
    selectCellText(control, () => editingCellRef.current);
  };

  const handleGridFocusOut = (event) => {
    const cell = event.target?.closest?.('td[data-row]');
    if (!cell || !event.relatedTarget || !cell.contains(event.relatedTarget)) {
      editingCellRef.current = false;
    }
  };

  const handleGridKeyDown = (event) => {
    if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (event.isComposing) return;
    const { key } = event;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.tagName !== 'INPUT' && target.tagName !== 'SELECT') return;
    const cell = target.closest('td[data-row][data-col]');
    if (!cell) return;

    const rowIndex = Number(cell.dataset.row);
    const colIndex = Number(cell.dataset.col);
    if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) return;

    const horizontal = key === 'ArrowLeft' || key === 'ArrowRight';
    if (
      horizontal &&
      editingCellRef.current &&
      !shouldLeaveTextCell(target, key === 'ArrowLeft' ? 'left' : 'right')
    ) {
      return;
    }

    const delta = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }[key];
    const nextRow = rowIndex + delta[0];
    const nextCol = colIndex + delta[1];
    if (nextRow < 0 || nextCol < 0 || nextRow >= visibleRows.length || nextCol >= columns.length) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    focusCell(nextRow, nextCol);
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / ORG_PAGE_SIZE));
  const showPageNav = usesOrgPaging && totalRecords > ORG_PAGE_SIZE;
  const rowNumberBase = usesOrgPaging ? (page - 1) * ORG_PAGE_SIZE : 0;
  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * ORG_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * ORG_PAGE_SIZE, totalRecords);

  const changePage = (nextPage) => {
    if (!usesOrgPaging || pageLoading) return;
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    Object.entries(saveTimers.current).forEach(([key, timer]) => {
      clearTimeout(timer);
      delete saveTimers.current[key];
      const pending = rowsRef.current.find((item) => item.localId === key);
      if (pending?.dirty) persistRow(pending);
    });
    if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0;
    setPage(nextPage);
  };

  if (loading) {
    return (
      <div className={styles.gridEmpty}>
        <div className={styles.spinner} />
        <p>Loading table...</p>
      </div>
    );
  }

  if (error && !schema) {
    return (
      <div className={styles.gridEmpty}>
        <p>{error}</p>
        <button type="button" className={styles.retryBtn} onClick={loadGrid}>
          Retry
        </button>
      </div>
    );
  }

  if (readOnly && rows.length === 0) {
    const tableLabel = schema?.table_name || 'This table';
    const previewColumns = columns.slice(0, 8);
    const hiddenColumnCount = columns.length - previewColumns.length;

    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon} aria-hidden="true">
          <Table2 size={22} strokeWidth={1.75} />
        </div>
        <h4 className={styles.emptyTitle}>No records yet</h4>
        <p className={styles.emptyText}>
          {tableLabel} has no rows for your account. Records show up here once they are linked to you.
        </p>
        {previewColumns.length > 0 && (
          <div className={styles.emptyColumns} aria-label="Table columns">
            {previewColumns.map((column) => (
              <span key={column.name} className={styles.emptyColumn}>
                {column.name}
              </span>
            ))}
            {hiddenColumnCount > 0 && (
              <span className={styles.emptyColumnMore}>+{hiddenColumnCount} more</span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!columns.length) {
    return (
      <div className={styles.gridEmpty}>
        <p>
          {onOpenEditFields
            ? 'No fields yet. Use Edit fields to add columns.'
            : 'No fields yet.'}
        </p>
        {onOpenEditFields && (
          <button type="button" className={styles.retryBtn} onClick={onOpenEditFields}>
            Edit fields
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.gridWrap}>
      {(savingKey || saveFlash || error) && (
        <div className={styles.toastStack} aria-live="polite">
          {error ? (
            <div className={`${styles.toast} ${styles.toastError}`}>{error}</div>
          ) : (
            <div className={`${styles.toast} ${styles.toastOk}`}>
              {savingKey ? 'Saving…' : 'Saved'}
            </div>
          )}
        </div>
      )}

      {showPageNav && (
        <div className={styles.tableToolbar}>
          <span className={styles.toolbarHint}>
            Showing {rangeStart}–{rangeEnd} of {totalRecords}
          </span>
          <div className={styles.paginationGroup}>
            <button
              type="button"
              className={styles.paginationButton}
              onClick={() => changePage(page - 1)}
              disabled={page <= 1 || pageLoading}
            >
              Previous
            </button>
            <span className={styles.paginationPage}>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className={styles.paginationButton}
              onClick={() => changePage(page + 1)}
              disabled={page >= totalPages || pageLoading}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <SearchFilterBar
        fields={searchColumns}
        optionsByField={filterOptions}
        values={filters}
        onChange={(fieldName, nextValue) => {
          setFilters((prev) => ({ ...prev, [fieldName]: nextValue }));
        }}
        onClear={() => setFilters({})}
      />

      <div
        className={`${styles.gridScroll} ${pageLoading ? styles.gridPaging : ''}`}
        ref={gridScrollRef}
      >
        <table
          className={`${styles.dataGrid} ${selectMode ? styles.selectMode : ''}`}
          onKeyDown={handleGridKeyDown}
          onMouseDown={handleGridPointerDown}
          onMouseUp={handleGridPointerUp}
          onBlur={handleGridFocusOut}
        >
          <colgroup>
            {selectMode ? <col className={styles.colSelectCol} /> : null}
            <col className={styles.rowNumCol} />
            {columns.map((column) => (
              <col key={column.name} style={{ width: columnWidths[column.name] }} />
            ))}
            {onOpenEditFields ? <col className={styles.addColCol} /> : null}
          </colgroup>
          <thead>
            <tr>
              {selectMode && (
                <th className={styles.colSelect}>
                  <div className={styles.colSelectInner}>
                    <input
                      type="checkbox"
                      className={styles.rowCheck}
                      checked={allPersistedSelected}
                      disabled={persistedRows.length === 0}
                      onChange={toggleAllSelected}
                      aria-label="Select all rows"
                    />
                  </div>
                </th>
              )}
              <th className={styles.rowNum}>#</th>
              {columns.map((column) => (
                <th
                  key={column.name}
                  style={{ width: columnWidths[column.name] }}
                >
                  <span className={styles.thLabel}>
                    {column.name}
                    {column.type === 'reference' && column.ref_schema_id && onSelectMaster && (
                      <button
                        type="button"
                        className={styles.refLink}
                        title="Open linked master"
                        onClick={() => onSelectMaster(column.ref_schema_id)}
                      >
                        ↗
                      </button>
                    )}
                  </span>
                </th>
              ))}
              {onOpenEditFields ? (
                <th className={styles.addColTh}>
                  <button
                    type="button"
                    className={styles.addColBtn}
                    title="Edit fields"
                    onClick={onOpenEditFields}
                  >
                    +
                  </button>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && hasActiveFilters ? (
              <tr>
                <td
                  className={styles.filterEmptyCell}
                  colSpan={
                    columns.length +
                    (selectMode ? 1 : 0) +
                    1 +
                    (onOpenEditFields ? 1 : 0)
                  }
                >
                  No rows match these filters
                </td>
              </tr>
            ) : null}
            {visibleRows.map((row, rowIndex) => {
              const blank = isBlankRow(row, columns);
              return (
                <tr
                  key={row.localId}
                  className={`${blank ? styles.blankRow : ''} ${
                    row.recordId && selectedIdSet.has(String(row.recordId))
                      ? styles.selectedRow
                      : ''
                  }`.trim()}
                >
                  {selectMode && (
                    <td className={styles.colSelect}>
                      <div className={styles.colSelectInner}>
                        {row.recordId ? (
                          <input
                            type="checkbox"
                            className={styles.rowCheck}
                            checked={selectedIdSet.has(String(row.recordId))}
                            onChange={() => toggleRowSelected(row.recordId)}
                            aria-label={`Select row ${rowIndex + 1}`}
                          />
                        ) : null}
                      </div>
                    </td>
                  )}
                  <td className={styles.rowNum}>
                    {usesOrgPaging && !row.recordId ? '' : rowNumberBase + rowIndex + 1}
                  </td>
                  {columns.map((column, colIndex) => {
                    const value = row.values?.[column.name];
                    if (column.type === 'reference') {
                      const selectedId =
                        typeof value === 'object'
                          ? value?.ref_record_id || ''
                          : value || '';
                      return (
                        <td
                          key={column.name}
                          style={{ width: columnWidths[column.name] }}
                          data-row={rowIndex}
                          data-col={colIndex}
                        >
                          <select
                            className={styles.gridCell}
                            value={selectedId}
                            title={displayCellValue(value, column)}
                            disabled={readOnly}
                            onChange={(e) =>
                              handleCellChange(row.localId, column, e.target.value)
                            }
                            onBlur={() => handleCellBlur(row.localId)}
                          >
                            <option value="">Search…</option>
                            {(referenceOptions[column.name] || []).map((option) => (
                              <option key={option.record_id} value={option.record_id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }

                    if (column.type === 'boolean') {
                      return (
                        <td
                          key={column.name}
                          style={{ width: columnWidths[column.name] }}
                          data-row={rowIndex}
                          data-col={colIndex}
                        >
                          <select
                            className={styles.gridCell}
                            value={displayCellValue(value, column)}
                            disabled={readOnly}
                            onChange={(e) =>
                              handleCellChange(row.localId, column, e.target.value)
                            }
                            onBlur={() => handleCellBlur(row.localId)}
                          >
                            <option value="" />
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={column.name}
                        style={{ width: columnWidths[column.name] }}
                        data-row={rowIndex}
                        data-col={colIndex}
                      >
                        <input
                          className={styles.gridCell}
                          type={inputTypeFor(column.type)}
                          value={displayCellValue(value, column)}
                          title={displayCellValue(value, column)}
                          readOnly={
                            readOnly ||
                            (column.seq === true && Boolean(displayCellValue(value, column)))
                          }
                          placeholder={
                            column.type === 'attachment' ? 'Attach file (soon)' : undefined
                          }
                          onChange={(e) =>
                            handleCellChange(row.localId, column, e.target.value)
                          }
                          onBlur={() => handleCellBlur(row.localId)}
                        />
                      </td>
                    );
                  })}
                  {onOpenEditFields ? <td className={styles.addColTh} /> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ErpDataGrid;
